/**
 * EmptyState Component
 *
 * Displays contextual empty states based on current category and search query.
 * Provides a call-to-action button for adding new items.
 */

import {
  Inbox,
  Link2,
  Headphones,
  FileText,
  Lightbulb,
  File,
  CheckCircle2,
  Star,
  Search,
  Plus,
  Layers,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { Button } from '@/components/ui/button';
import type { Category } from '@/types';

// ============================================
// TYPES
// ============================================

export interface EmptyStateProps {
  /** Current category or filter */
  category?: Category | undefined;
  /** Active search query */
  searchQuery?: string | undefined;
  /** Handler for the add button */
  onAddClick?: (() => void) | undefined;
  /** Additional class names */
  className?: string | undefined;
}

// ============================================
// CONFIGURATION
// ============================================

interface EmptyStateConfig {
  icon: typeof Inbox;
  title: string;
  description: string;
  actionLabel?: string;
  iconColor: string;
  iconBg: string;
}

const categoryConfig: Record<Category, EmptyStateConfig> = {
  all: {
    icon: Layers,
    title: 'No items yet',
    description:
      'Start building your personal knowledge base by adding links, notes, ideas, and more.',
    actionLabel: 'Add your first item',
    iconColor: 'text-brand-600',
    iconBg: 'bg-brand-50',
  },
  inbox: {
    icon: Inbox,
    title: 'Inbox is empty',
    description: 'Items tagged with "inbox" will appear here for quick processing.',
    actionLabel: 'Add to inbox',
    iconColor: 'text-amber-600',
    iconBg: 'bg-amber-50',
  },
  link: {
    icon: Link2,
    title: 'No links saved',
    description: 'Save interesting articles, videos, and web pages to read later.',
    actionLabel: 'Save a link',
    iconColor: 'text-blue-600',
    iconBg: 'bg-blue-50',
  },
  podcast: {
    icon: Headphones,
    title: 'No podcasts saved',
    description: 'Keep track of podcast episodes you want to listen to.',
    actionLabel: 'Add a podcast',
    iconColor: 'text-purple-600',
    iconBg: 'bg-purple-50',
  },
  note: {
    icon: FileText,
    title: 'No notes yet',
    description: 'Capture your thoughts, meeting notes, and important information.',
    actionLabel: 'Create a note',
    iconColor: 'text-green-600',
    iconBg: 'bg-green-50',
  },
  idea: {
    icon: Lightbulb,
    title: 'No ideas captured',
    description: 'Write down your ideas before they slip away.',
    actionLabel: 'Capture an idea',
    iconColor: 'text-amber-600',
    iconBg: 'bg-amber-50',
  },
  file: {
    icon: File,
    title: 'No files uploaded',
    description: 'Upload documents and files to keep everything in one place.',
    actionLabel: 'Upload a file',
    iconColor: 'text-gray-600',
    iconBg: 'bg-gray-50',
  },
  task: {
    icon: CheckCircle2,
    title: 'No tasks created',
    description: 'Track your to-dos and get things done.',
    actionLabel: 'Create a task',
    iconColor: 'text-teal-600',
    iconBg: 'bg-teal-50',
  },
  favorites: {
    icon: Star,
    title: 'No favorites yet',
    description: 'Star items you want quick access to and they will appear here.',
    iconColor: 'text-amber-500',
    iconBg: 'bg-amber-50',
  },
};

const searchEmptyState: EmptyStateConfig = {
  icon: Search,
  title: 'No results found',
  description: 'Try adjusting your search terms or filters.',
  iconColor: 'text-gray-400',
  iconBg: 'bg-gray-100',
};

// ============================================
// MAIN COMPONENT
// ============================================

export function EmptyState({
  category = 'all',
  searchQuery,
  onAddClick,
  className,
}: EmptyStateProps) {
  // Determine which config to use
  const isSearching = Boolean(searchQuery?.trim());
  const config = isSearching ? searchEmptyState : categoryConfig[category];

  const Icon = config.icon;

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center',
        'px-4 py-12 animate-fade-in-up',
        className
      )}
      role="status"
      aria-label={config.title}
    >
      {/* Icon */}
      <div className={cn('flex items-center justify-center rounded-2xl p-5 mb-5', config.iconBg)}>
        <Icon className={cn('h-12 w-12', config.iconColor)} aria-hidden="true" />
      </div>

      {/* Title */}
      <h3 className="text-xl font-display font-semibold text-gray-900 mb-2">
        {isSearching ? `No results for "${searchQuery}"` : config.title}
      </h3>

      {/* Description */}
      <p className="text-gray-500 max-w-sm mb-2">{config.description}</p>

      {/* Warm encouragement (non-search only) */}
      {!isSearching && !['favorites'].includes(category) && (
        <p className="text-sm text-gray-400 mb-6">It only takes a few seconds to get started.</p>
      )}
      {!isSearching && ['favorites'].includes(category) && <div className="mb-6" />}

      {/* Action button */}
      {!isSearching && config.actionLabel && onAddClick && (
        <Button onClick={onAddClick} className="gap-2">
          <Plus className="h-4 w-4" />
          {config.actionLabel}
        </Button>
      )}

      {/* Search suggestions */}
      {isSearching && (
        <div className="text-sm text-gray-500 space-y-1 mt-4">
          <p>Suggestions:</p>
          <ul className="list-disc list-inside text-left">
            <li>Check your spelling</li>
            <li>Try more general keywords</li>
            <li>Remove filters to broaden your search</li>
          </ul>
        </div>
      )}
    </div>
  );
}

// ============================================
// INLINE EMPTY STATE (for smaller areas)
// ============================================

interface InlineEmptyStateProps {
  message: string;
  icon?: typeof Inbox | undefined;
  action?:
    | {
        label: string;
        onClick: () => void;
      }
    | undefined;
  className?: string | undefined;
}

export function InlineEmptyState({
  message,
  icon: Icon = Inbox,
  action,
  className,
}: InlineEmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center',
        'px-4 py-8 rounded-lg border border-dashed border-gray-300',
        'bg-gray-50',
        className
      )}
    >
      <Icon className="h-8 w-8 text-gray-400 mb-2" aria-hidden="true" />
      <p className="text-sm text-gray-500 mb-3">{message}</p>
      {action && (
        <Button variant="outline" size="sm" onClick={action.onClick}>
          {action.label}
        </Button>
      )}
    </div>
  );
}

// ============================================
// LOADING STATE
// ============================================

interface LoadingStateProps {
  message?: string | undefined;
  className?: string | undefined;
}

export function LoadingState({ message = 'Loading...', className }: LoadingStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center',
        'px-4 py-16',
        className
      )}
      role="status"
      aria-live="polite"
    >
      {/* Spinner */}
      <div
        className={cn(
          'h-10 w-10 animate-spin rounded-full',
          'border-4 border-gray-200 border-t-brand-600',
          'mb-4'
        )}
      />

      {/* Message */}
      <p className="text-gray-500">{message}</p>
    </div>
  );
}

// ============================================
// ERROR STATE
// ============================================

interface ErrorStateProps {
  title?: string | undefined;
  message: string;
  onRetry?: (() => void) | undefined;
  className?: string | undefined;
}

export function ErrorState({
  title = 'Something went wrong',
  message,
  onRetry,
  className,
}: ErrorStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center',
        'px-4 py-16',
        className
      )}
      role="alert"
    >
      {/* Icon */}
      <div className="flex items-center justify-center rounded-full p-4 mb-4 bg-red-50">
        <svg
          className="h-10 w-10 text-red-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      </div>

      {/* Title */}
      <h3 className="text-lg font-display font-semibold text-gray-900 mb-2">{title}</h3>

      {/* Message */}
      <p className="text-gray-500 max-w-sm mb-6">{message}</p>

      {/* Retry button */}
      {onRetry && (
        <Button onClick={onRetry} variant="outline">
          Try again
        </Button>
      )}
    </div>
  );
}
